import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminRoute from './components/AdminRoute';
import LoadingSpinner from './components/LoadingSpinner';
import SessionManager from './components/SessionManager';
import ErrorBoundary from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';

// Lazy loaded page components to reduce initial bundle size and optimize performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Events = lazy(() => import('./pages/Events'));
const Cities = lazy(() => import('./pages/Cities'));
const Countries = lazy(() => import('./pages/Countries'));
const Categories = lazy(() => import('./pages/Categories'));
const Companies = lazy(() => import('./pages/Companies'));
const Users = lazy(() => import('./pages/Users'));
const Login = lazy(() => import('./pages/Login'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const InsufficientPermissions = lazy(() => import('./pages/InsufficientPermissions'));
const Slider = lazy(() => import('./components/Slider'));

// Main app content (protected)
const AppContent = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner message="Initializing Admin Panel..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <>
      <SessionManager />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Sidebar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - 264px)` },
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
            mt: { xs: '60px', md: '68px' },
          }}
        >
          <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/events" element={<Events />} />
              {/* <Route path="/orders" element={<ErrorBoundary><Orders /></ErrorBoundary>} /> */}
              <Route path="/cities" element={<ErrorBoundary><Cities /></ErrorBoundary>} />
              <Route path="/countries" element={<ErrorBoundary><Countries /></ErrorBoundary>} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/companies" element={<ErrorBoundary><Companies /></ErrorBoundary>} />
              <Route path="/users" element={<Users />} />
              <Route path="/slider" element={<Slider />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

// Main app component
function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<LoadingSpinner message="Loading..." />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/insufficient-permissions" element={<InsufficientPermissions />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
